import React, { useState, useEffect } from "react";
import axiosInstance from "../../../helpers/axios";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { useAiCreditsContext } from "../../../context/AiCreditsContext";
import AiCreditConfirm from "../../shared/AiCreditConfirm/AiCreditConfirm";
import "./AiRuleAssistant.css";

const EXAMPLE_PROMPTS = [
  "Approve if FOIR below 50% and CIBIL above 720",
  "Send to manual review if age below 22",
  "Reject if state is not AP or Telangana",
  "Approve if salary above 40000 and no active loans",
];

/**
 * AI Rule Assistant — NL text input to generate structured rules.
 * Includes conflict/redundancy detection and plain-english explanation.
 */
export default function AiRuleAssistant({ onRuleAccepted }) {
  const decode = useCurrentUser();
  const { balance, deductCredits } = useAiCreditsContext();
  const [nlInput, setNlInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [generatedRules, setGeneratedRules] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [explanation, setExplanation] = useState("");
  const [error, setError] = useState(null);
  const [showCreditConfirm, setShowCreditConfirm] = useState(false);
  const [monitorSummary, setMonitorSummary] = useState(null);

  const role = decode?.role;
  const hasAccess = ["admin", "crm", "super_admin"].includes(role);
  const isAdmin = ["admin", "super_admin"].includes(role);

  useEffect(() => {
    if (!isAdmin) return;
    axiosInstance.get("/admin/ai/rules/monitor/summary")
      .then(res => setMonitorSummary(res?.data?.data || null))
      .catch(() => {});
  }, [isAdmin]);

  if (!hasAccess) return null;

  const handleGenerate = async () => {
    if (!nlInput.trim()) return;
    // Show credit confirmation before proceeding
    setShowCreditConfirm(true);
  };

  const executeGenerate = async () => {
    setShowCreditConfirm(false);
    setLoading(true);
    setError(null);
    setGeneratedRules(null);
    setConflicts(null);
    setExplanation("");

    try {
      const res = await axiosInstance.post("/admin/ai/rules/generate", { message: nlInput.trim() });
      const rules = res?.data?.data || res?.data?.rules || res?.data || [];
      setGeneratedRules(rules);
      setExplanation(res?.data?.explanation || "");

      // Deduct credit only after successful generation
      await deductCredits({ action: "rule_generate", creditsUsed: 1, metadata: nlInput.trim() });

      // Auto-check conflicts
      if (rules.length > 0) {
        checkConflicts(rules);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to generate rule. No credits were charged.");
    } finally {
      setLoading(false);
    }
  };

  const checkConflicts = async (ruleArray) => {
    try {
      const res = await axiosInstance.post("/admin/ai/rules/conflicts", { ruleArray });
      const conflictData = res?.data?.data || res?.data?.conflicts || res?.data || [];
      if (conflictData.length > 0) {
        setConflicts(conflictData);
      }
    } catch (err) {
      // Non-critical — don't block the flow
    }
  };

  const handleAccept = () => {
    if (onRuleAccepted && generatedRules) {
      onRuleAccepted(generatedRules);
    }
    setGeneratedRules(null);
    setConflicts(null);
    setNlInput("");
    setExplanation("");
  };

  const handleDiscard = () => {
    setGeneratedRules(null);
    setConflicts(null);
    setExplanation("");
  };

  const handleExampleClick = (example) => {
    setNlInput(example);
  };

  return (
    <div className="ai-rule-section">
      <h3 className="ai-rule-title">🤖 Rule Assistant — Create Rules in Plain English</h3>
      <div className="ai-rule-subtitle">Describe your rule naturally and AI will convert it to a decision table format</div>

      {/* Monitor Status Badge - admin only */}
      {isAdmin && monitorSummary && monitorSummary.totalPolicies > 0 && (
        <div className="ai-rule-monitor-badge">
          <span className="ai-rule-monitor-label">Policy Health:</span>
          <span className="ai-rule-monitor-healthy">✓ {monitorSummary.healthyPolicies} healthy</span>
          {monitorSummary.warningPolicies > 0 && <span className="ai-rule-monitor-warning">⚠ {monitorSummary.warningPolicies} redundant</span>}
          {monitorSummary.criticalPolicies > 0 && <span className="ai-rule-monitor-critical">✗ {monitorSummary.criticalPolicies} conflicts</span>}
          <span className="ai-rule-monitor-time">Last scan: {monitorSummary.lastRunAt ? new Date(monitorSummary.lastRunAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "Never"}</span>
        </div>
      )}

      <div className="ai-rule-input-wrapper">
        <input
          className="ai-rule-input"
          type="text"
          placeholder="e.g. Reject if CIBIL score is below 650 and applicant is self-employed..."
          value={nlInput}
          onChange={(e) => setNlInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleGenerate(); }}
        />
        <button className="ai-rule-btn" onClick={handleGenerate} disabled={loading || !nlInput.trim()}>
          {loading ? "Generating..." : "✨ Generate Rule"}
        </button>
      </div>

      <div className="ai-rule-examples">
        {EXAMPLE_PROMPTS.map((ex, i) => (
          <span key={i} className="ai-rule-example" onClick={() => handleExampleClick(ex)}>{ex}</span>
        ))}
      </div>

      {error && <div className="ai-rule-error">{error}</div>}

      {/* Generated Rule Preview */}
      {generatedRules && generatedRules.length > 0 && (
        <div className="ai-rule-generated">
          <h4 className="ai-rule-generated-title">✅ AI-Generated Rule (Review before saving)</h4>
          <p className="ai-rule-generated-source">From: "{nlInput}"</p>

          <table className="ai-rule-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Source</th>
                <th>Data Element</th>
                <th>Operator</th>
                <th>Value</th>
                <th>If True</th>
                <th>If False</th>
              </tr>
            </thead>
            <tbody>
              {generatedRules.map((rule, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td>{rule.source || "—"}</td>
                  <td>{rule.dataElement || rule.element || "—"}</td>
                  <td>{rule.operator || rule.op || "—"}</td>
                  <td>{rule.value || "—"}</td>
                  <td style={{ color: rule.ifTrue?.toLowerCase() === "reject" ? "#dc2626" : "#166534", fontWeight: 600 }}>
                    {rule.ifTrue || rule.outcome1 || "—"}
                  </td>
                  <td>{rule.ifFalse || rule.outcome2 || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {explanation && (
            <div className="ai-rule-explanation-box">
              <strong>Plain English Explanation:</strong> {explanation}
            </div>
          )}

          <div className="ai-rule-actions">
            <button className="ai-rule-action-btn ai-rule-accept" onClick={handleAccept}>✓ Add to Policy</button>
            <button className="ai-rule-action-btn ai-rule-modify" onClick={() => setGeneratedRules(null)}>✏️ Modify</button>
            <button className="ai-rule-action-btn ai-rule-reject" onClick={handleDiscard}>✕ Discard</button>
          </div>
        </div>
      )}

      {/* Conflict Detection */}
      {conflicts && conflicts.length > 0 && (
        <div className="ai-rule-conflict-panel">
          <h4 className="ai-rule-conflict-title">⚠️ Potential Conflicts Detected</h4>
          {conflicts.map((c, i) => (
            <div key={i} className="ai-rule-conflict-item">
              <div className="ai-rule-conflict-type">{c.type || "Conflict"}</div>
              <div className="ai-rule-conflict-desc">{c.description || c.message || "Potential overlap with existing policy"}</div>
              {c.existingRule && <div className="ai-rule-conflict-existing">Existing: {c.existingRule}</div>}
            </div>
          ))}
        </div>
      )}

      <AiCreditConfirm
        isOpen={showCreditConfirm}
        action="Generate Rule from Natural Language"
        credits={1}
        remainingCredits={balance?.remainingCredits}
        userName={decode?.display_name}
        onCancel={() => setShowCreditConfirm(false)}
        onConfirm={executeGenerate}
      />
    </div>
  );
}
